import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Project } from '@/types'

import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { Layout } from '@/components/generals/layout'
import { SectionLabel } from '@/components/generals/section-label'
import { Seo } from '@/components/generals/seo'
import { WorkList } from '@/components/work/work-list'
import { getPublishedProjects, shouldKeepLastGoodPage } from '@/lib/projects'

interface WorkPageProps {
    projects: Project[]
}

// The default covers the moment during a live code update when the page renders before its data is re-fetched.
const WorkPage: FC<WorkPageProps> = ({ projects = [] }): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Work"
                description="Selected websites, web apps and creative coding projects by Ansam Yaseen, a creative full-stack web developer."
            />

            <section className='relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-48'>
                <div aria-hidden='true' className='bg-grid absolute inset-0 -z-10' />

                <Container>
                    <SectionLabel index='00' label="Archive" className='hero-in' />

                    <h1 className='hero-in mt-6 font-heading text-6xl font-bold tracking-[-0.04em] [--d:0.08s] md:text-8xl xl:text-9xl'>
                        All{' '}
                        <span className='font-serif font-normal italic text-brand-bright'>
                            work
                        </span>

                        <sup className='ml-2 align-super font-mono text-lg font-normal tracking-normal text-muted-foreground md:text-2xl'>
                            ({String(projects.length).padStart(2, '0')})
                        </sup>
                    </h1>

                    <p className='hero-in mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground [--d:0.16s]'>
                        Websites, web apps, storefronts and creative experiments. Every project is a chance to make something faster, clearer and more delightful than before.
                    </p>
                </Container>
            </section>

            <section aria-label="Project list" className='pb-24 md:pb-36'>
                <Container>
                    {projects.length > 0 ? (
                        <WorkList projects={projects} />
                    ) : (
                        <div className='rounded-2xl border border-dashed border-border px-6 py-16 text-center'>
                            <p className='text-xl font-semibold'>
                                New case studies are on their way.
                            </p>

                            <p className='mx-auto mt-3 max-w-md text-muted-foreground'>
                                In the meantime, I would love to hear about the project you have in mind.
                            </p>

                            <ButtonLink href='/#contact' className='mt-8'>
                                Start a conversation
                            </ButtonLink>
                        </div>
                    )}
                </Container>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkPageProps> = async () => {
    try {
        return {
            props: { projects: await getPublishedProjects() },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Work page: could not load projects.', error instanceof Error ? error.message : error)

        if (shouldKeepLastGoodPage()) throw error

        return {
            props: { projects: [] },
            revalidate: 30
        }
    }
}

export default WorkPage
